<template>
  <v-container fluid>
    <v-data-table
      :headers="headers"
      :items="userList"
      :items-per-page="10"
      class="elevation-1 mt-2"
      :hide-default-footer="true"
      dense
      disable-pagination
    >
      <template v-slot:top>
        <v-toolbar
          flat
        >
          <v-toolbar-title>List Users</v-toolbar-title>
          <v-divider
            class="mx-4" inset vertical
          ></v-divider>
          <v-col cols="2" class="tmc-global-config">
            <v-row>
              <v-select
                :items="timePeriodList"
                v-model="globalSettingItem.logoutTimePeriod"
                label="Logout Time Period"
                item-value="timePeriod"
                item-text="description"
                dense
                class="mx-2"
              ></v-select>
            </v-row>
          </v-col>
          <v-col cols="2" class="tmc-global-config">
            <v-row>
              <v-select
                :items="passExpList"
                v-model="globalSettingItem.passExpPeriod"
                label="Password Expiration Period"
                item-value="expPeriod"
                item-text="description"
                dense
                class="mx-2"
              ></v-select>
            </v-row>
          </v-col>
          <v-col cols="2" class="tmc-global-config">
            <v-row>
              <v-text-field
                v-model.number="globalSettingItem.minPasswordLength"
                label="Minimum Password Length"
                type="number"
                min="0"
                dense
                class="mx-2"
              ></v-text-field>
            </v-row>
          </v-col>
          <v-col cols="auto">
            <v-row>
              <v-btn
                small
                color="primary"
                @click="updateConfig"
              >Update Config
            </v-btn>
            </v-row>
          </v-col>
          <v-btn
            small
            outlined color="primary"
            class="ml-2"
            @click="printUserList"
          >Print User List
          </v-btn>
          <v-spacer></v-spacer>
          <NewEditDialog
            :canAdd="true"
          />
        </v-toolbar>
      </template>
      <template v-slot:[`item.roles`]="{ item }">
        <td>{{item.roles.map((r) => rolesConst[r])}}</td>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-icon
          small class="mr-2 tmc-edit-user-icon"
          @click="editItem(item)"
        >
          {{icons.mdiPencil}}
        </v-icon>
        <v-icon
          small class="mr-2"
          @click="deleteItem(item)"
        >
          {{icons.mdiDelete}}
        </v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import {
  mdiDelete,
  mdiPencil,
} from '@mdi/js';
import { mapState, mapGetters } from 'vuex';
import NewEditDialog from './NewEditDialog.vue';
import {
  usersLogoutTimePeriods,
  usersPassExpPeriods,
} from '../../util/shared/tmc-global';
import { clients } from '../../util/clients';
import { pickUsersListGlobalFields } from '../../util/shared/system-config-scopes';
import { cloneItem } from '../../util/shared/vue-global';
import { getRawJwt } from '../../util/jwt';

const { backendRest } = clients.direct;

const TM_ADMIN_ROLE_DESCRIPTIONS = [
  'TM Consulting Support / Administrative role',
  'TM Restore Database',
];

// eslint-disable-next-line max-len
/** JWT `roles` claim uses PascalCase (see `roleGroups.js`); `rolesConst` keys are GraphQL enum names (e.g. TMCSUPPORT). */
const TM_ADMIN_JWT_ROLES = ['TmcSupport', 'TmcRestoreDb', 'TmcRestoreDatabase'];

export default {
  name: 'UserList',
  components: {
    NewEditDialog,
  },
  data: () => ({
    originalGlobalSetting: null,
    icons: {
      mdiDelete,
      mdiPencil,
    },
    headers: [
      { text: 'User', value: 'username' },
      { text: 'Roles', value: 'roles' },
      { text: 'wkst #', value: 'userWkst' },
      { text: 'Action', value: 'actions', sortable: false },
    ],
    timePeriodList: usersLogoutTimePeriods,
    passExpList: usersPassExpPeriods,
  }),
  async created() {
    this.$store.dispatch('UserAdministration/uploadMkdir');
    await this.$store.dispatch(
      'SystemConfig/loadConfig',
      { requiredScope: 'global' },
    );
    this.originalGlobalSetting = cloneItem(this.globalSettingItem);
  },
  computed: {
    ...mapState({
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem || {},
    }),
    ...mapGetters('auth/token', ['authUserRoles']),
    // eslint-disable-next-line max-len
    /** Matches TM admin visibility: TmcSupport / restore-DB roles (JWT strings, not `rolesConst` keys). */
    viewerHasTmAdminAccess() {
      const roles = this.authUserRoles || [];
      return roles.some((r) => TM_ADMIN_JWT_ROLES.includes(r));
    },
    userList() {
      const allUsers = this.$store.state.UserAdministration.userList;
      if (this.viewerHasTmAdminAccess) {
        return allUsers;
      }
      const rolesMap = this.rolesConst;
      return allUsers.filter((user) => {
        const hasTmSupportRole = (user.roles || []).some(
          (r) => TM_ADMIN_ROLE_DESCRIPTIONS.includes(rolesMap[r]),
        );
        return !(user.isSystemUser || hasTmSupportRole);
      });
    },
    rolesConst() {
      return this.$store.state.UserAdministration.rolesConst;
    },
  },
  methods: {
    /**
     * Full payload for save: store snapshot merged with fields edited on this screen.
     */
    mergeGlobalConfigForSave() {
      return {
        ...cloneItem(this.globalSettingItem),
        ...this.buildGlobalConfigItem(),
      };
    },
    buildGlobalConfigItem() {
      return pickUsersListGlobalFields(this.globalSettingItem);
    },
    editItem(item) {
      this.$store.commit('UserAdministration/setEditedIndex', this.userList.indexOf(item));
      this.$store.commit('UserAdministration/setEditedItem', item);
      this.$store.commit('UserAdministration/setDialog', true);
    },
    async deleteItem(item) {
      if (window.confirm('Remove this user? They will disappear from this list and will not be able to sign in.')) {
        this.$store.dispatch('UserAdministration/deleteUser', item);
      }
    },
    async updateConfig() {
      if (window.confirm('Are you sure want to update this configuration?')) {
        const payload = {
          calledScope: 'global',
          item: this.mergeGlobalConfigForSave(),
          previousItem: this.originalGlobalSetting,
          isLogAudit: true,
        };
        const ok = await this.$store.dispatch('SystemConfig/updateConfig', payload);
        if (ok) {
          this.originalGlobalSetting = cloneItem(this.globalSettingItem);
        }
      }
    },
    printUserList() {
      const url = `${backendRest.defaults.baseURL}/user-admin/user-list-report?token=${getRawJwt()}`;
      window.open(url, '_blank');
    },
  },
};
</script>

<style lang="sass">
.tmc-global-config
  margin-top: 2%

</style>
